import { msg } from '@lingui/core/macro';
import type { MessageDescriptor } from '@lingui/core';
import { formatMoneyCents } from '@/lib/money';

// Late extras after invoicing (#2028): extras that arrived after the invoice
// that would have carried them was issued. One open workflow item of category
// late_extras per organisation lists them.

export type LateExtraKind = 'extra_day' | 'child_extra' | 'course_attendance';

export interface LateExtrasInvoiceRef {
  invoice_id: string;
  invoice_number: string;
  /** YYYY-MM */
  period: string;
}

export interface LateExtrasItem {
  kind: LateExtraKind | string;
  id: string;
  child_id: string;
  child_name: string;
  /** YYYY-MM-DD */
  service_date: string;
  /** null when it cannot be priced without the invoice engine. */
  amount_rappen: number | null;
  label: string;
  would_have_been_on: LateExtrasInvoiceRef;
}

export interface LateExtrasInvoiceResult {
  replacement_invoice_id?: string;
  replacement_number?: string;
  error_code?: string;
  error_reasons?: string[];
  at?: string;
}

export interface LateExtrasInvoiceGroup {
  invoice_id: string;
  invoice_number: string;
  period: string;
  child_name: string;
  item_count: number;
  sum_rappen: number;
  has_unpriced: boolean;
  result: LateExtrasInvoiceResult | null;
}

export interface LateExtrasPayloadModel {
  items: LateExtrasItem[];
  item_count: number;
  sum_rappen: number;
  has_unpriced: boolean;
  by_invoice: LateExtrasInvoiceGroup[];
  regenerate: { at: string; by: string; replacements: number } | null;
  regenerating?: { at: string; by: string } | null;
  resolved_by?: string;
  reason?: string;
}

function asArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

/** Reads a late_extras payload defensively (an older or partial payload never throws). */
export function parseLateExtrasPayload(payload: Record<string, unknown> | undefined): LateExtrasPayloadModel {
  const items = asArray<LateExtrasItem>(payload?.items);
  return {
    items,
    item_count: typeof payload?.item_count === 'number' ? payload.item_count : items.length,
    sum_rappen: typeof payload?.sum_rappen === 'number' ? payload.sum_rappen : 0,
    has_unpriced: payload?.has_unpriced === true,
    by_invoice: asArray<LateExtrasInvoiceGroup>(payload?.by_invoice),
    regenerate: (payload?.regenerate as LateExtrasPayloadModel['regenerate']) ?? null,
    regenerating: (payload?.regenerating as LateExtrasPayloadModel['regenerating']) ?? null,
    resolved_by: typeof payload?.resolved_by === 'string' ? payload.resolved_by : undefined,
    reason: typeof payload?.reason === 'string' ? payload.reason : undefined,
  };
}

export const LATE_EXTRA_KIND_LABELS: Record<LateExtraKind, MessageDescriptor> = {
  extra_day: msg({ id: 'lateExtras.kind.extraDay', message: 'Extra day' }),
  child_extra: msg({ id: 'lateExtras.kind.childExtra', message: 'Extra' }),
  course_attendance: msg({ id: 'lateExtras.kind.courseAttendance', message: 'Course' }),
};

type Translate = { _: (d: MessageDescriptor) => string };
type TranslateWithValues = { _: (d: { id: string; message: string; values?: Record<string, unknown> }) => string };

export function lateExtraKindLabel(i18n: Translate, kind: string): string {
  const descriptor = LATE_EXTRA_KIND_LABELS[kind as LateExtraKind];
  return descriptor ? i18n._(descriptor) : kind;
}

/** "2026-09" in the reader's locale: "September 2026". */
export function formatPeriod(period: string, locale: string): string {
  const match = /^(\d{4})-(\d{2})$/.exec(period);
  if (!match) return period;
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1));
  return new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(date);
}

/**
 * The period most of the extras would have been billed on, or null when the
 * extras spread over several periods equally (the title then says "several
 * invoices").
 */
export function dominantPeriod(items: LateExtrasItem[]): string | null {
  const counts = new Map<string, number>();
  for (const it of items) {
    const p = it.would_have_been_on?.period;
    if (p) counts.set(p, (counts.get(p) ?? 0) + 1);
  }
  if (counts.size === 0) return null;
  if (counts.size === 1) return [...counts.keys()][0];
  const sorted = [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  return sorted[0][1] > sorted[1][1] ? sorted[0][0] : null;
}

/** The money line: "CHF 135.00", or "at least CHF 90.00" when an extra is unpriced. */
export function lateExtrasSumText(i18n: TranslateWithValues, sumRappen: number, hasUnpriced: boolean): string {
  const amount = formatMoneyCents(sumRappen, null);
  return hasUnpriced
    ? i18n._({ id: 'lateExtras.sum.atLeast', message: 'at least {amount}', values: { amount } })
    : amount;
}

/** The inbox and board line: how many, how much, which month. */
export function lateExtrasSummary(
  i18n: Translate & TranslateWithValues & { locale: string },
  payload: Record<string, unknown> | undefined,
): string {
  const p = parseLateExtrasPayload(payload);
  const period = dominantPeriod(p.items);
  const where = period
    ? formatPeriod(period, i18n.locale)
    : i18n._(msg({ id: 'lateExtras.summary.severalInvoices', message: 'several invoices' }));
  return i18n._({
    id: 'lateExtras.summary.line',
    message: '{count, plural, one {# new extra} other {# new extras}} · {sum} · {where}',
    values: { count: p.item_count, sum: lateExtrasSumText(i18n, p.sum_rappen, p.has_unpriced), where },
  });
}
